import { useState, useEffect, useCallback } from 'react';
import { useDropzone, type FileRejection } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { useUserGeneratedContent } from '../../../../models/user-generated-contents/context';
import useMediaUploader from '../../../../common/use-media-uploader';
import type {
  ContentImage,
  MediaGroup,
} from '../../../../models/user-generated-contents/types';

export const MEDIA_GROUP_TYPE = 'images' as const;
export const MAX_IMAGES = 10;
export const MAX_IMAGE_SIZE_MB = 10;
export const MAX_IMAGE_SIZE = MAX_IMAGE_SIZE_MB * 1024 * 1024;
export const ACCEPTED_IMAGE_TYPES = 'image/jpeg, image/png, image/gif';

export type UploaderError = { title: string; description: string };

export type UseImageUploaderReturn = {
  images: ContentImage[];
  inProgress: File[];
  totalCount: number;
  isAtLimit: boolean;
  isUploading: boolean;
  errors: UploaderError[];
  clearErrors: () => void;
  getInputProps: ReturnType<typeof useDropzone>['getInputProps'];
  open: ReturnType<typeof useDropzone>['open'];
  handleDropAccepted: (files: File[]) => void;
  handleDropRejected: (rejections: FileRejection[]) => void;
  handleDelete: (index: number) => void;
  handleReorder: (images: ContentImage[]) => void;
};

const useImageUploader = (): UseImageUploaderReturn => {
  const { t } = useTranslation();
  const { userGeneratedContent, updateAttachmentGroup } =
    useUserGeneratedContent();

  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [currentFile, setCurrentFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<UploaderError[]>([]);

  const attachments = userGeneratedContent.attachments ?? [];
  const imagesGroup = attachments.find(
    (g): g is Extract<MediaGroup, { type: typeof MEDIA_GROUP_TYPE }> =>
      g.type === MEDIA_GROUP_TYPE
  );
  const images: ContentImage[] = imagesGroup?.items ?? [];

  const totalCount =
    images.length + (currentFile ? 1 : 0) + pendingFiles.length;
  const isAtLimit = totalCount >= MAX_IMAGES;

  const { status } = useMediaUploader({
    file: currentFile,
    onStatusChange: undefined,
    onComplete: (url: string) => {
      updateAttachmentGroup(MEDIA_GROUP_TYPE, (prev) => [
        ...prev,
        { url, order: prev.length },
      ]);
      setCurrentFile(null);
    },
    onError: () => {
      setErrors([
        {
          title: '',
          description: t('user_generated_content.media.images.error_upload'),
        },
      ]);
      setCurrentFile(null);
    },
  });

  const isUploading = status === 'fetching' || status === 'uploading';

  useEffect(() => {
    if (!currentFile && pendingFiles.length > 0) {
      const [next, ...rest] = pendingFiles;
      setCurrentFile(next);
      setPendingFiles(rest);
    }
  }, [currentFile, pendingFiles]);

  const handleDropAccepted = useCallback(
    (files: File[]) => {
      const available = MAX_IMAGES - totalCount;
      if (files.length > available) {
        setErrors([
          {
            title: t('common.error_rejected_title'),
            description: t(
              'user_generated_content.media.images.error_too_many_files',
              { max: MAX_IMAGES }
            ),
          },
        ]);
      } else {
        setErrors([]);
      }
      setPendingFiles((prev) => [...prev, ...files.slice(0, available)]);
    },
    [t, totalCount]
  );

  const handleDropRejected = useCallback(
    (rejections: FileRejection[]) => {
      const errors = new Map<string, UploaderError>();
      for (const rejection of rejections) {
        const code = rejection?.errors[0]?.code;
        if (code === 'too-many-files') {
          errors.set('too-many-files', {
            title: t('common.error_rejected_title'),
            description: t(
              'user_generated_content.media.images.error_too_many_files',
              { max: MAX_IMAGES }
            ),
          });
        } else {
          errors.set('rejected', {
            title: t('common.error_rejected_title'),
            description: t(
              'user_generated_content.media.images.error_rejected',
              { maxSize: MAX_IMAGE_SIZE_MB }
            ),
          });
        }
      }
      setErrors(Array.from(errors.values()));
    },
    [t]
  );

  const { getInputProps, open } = useDropzone({
    accept: ACCEPTED_IMAGE_TYPES,
    maxSize: MAX_IMAGE_SIZE,
    maxFiles: MAX_IMAGES,
    multiple: true,
    noClick: true,
    noDrag: true,
    disabled: isAtLimit,
    onDropAccepted: handleDropAccepted,
    onDropRejected: handleDropRejected,
  });

  const handleDelete = useCallback(
    (index: number) => {
      updateAttachmentGroup(MEDIA_GROUP_TYPE, (prev) => {
        const next = prev
          .filter((_, i) => i !== index)
          .map((img, i) => ({ ...img, order: i }));
        return next.length > 0 ? next : undefined;
      });
      setErrors([]);
    },
    [updateAttachmentGroup]
  );

  const handleReorder = useCallback(
    (reordered: ContentImage[]) => {
      updateAttachmentGroup(MEDIA_GROUP_TYPE, () => reordered);
      setErrors([]);
    },
    [updateAttachmentGroup]
  );

  const clearErrors = useCallback(() => setErrors([]), []);

  const inProgress = currentFile
    ? [currentFile, ...pendingFiles]
    : pendingFiles;

  return {
    images,
    inProgress,
    totalCount,
    isAtLimit,
    isUploading,
    errors,
    clearErrors,
    getInputProps,
    open,
    handleDropAccepted,
    handleDropRejected,
    handleDelete,
    handleReorder,
  };
};

export default useImageUploader;
